import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { COOKIES } from '../../../common/constants/constants';
import { LoggerService } from '../logger/logger.service';
import { catchError, Observable,  of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class ZciCookieService {
    private readonly cookieService = inject(CookieService);
    private readonly logger = inject(LoggerService);
    private readonly httpClient = inject(HttpClient);


    /**
     * Gets the SPOID (Scrambled Person Object ID) from browser cookies.
     * 
     * @returns The SPOID value or null if not found
     * 
     */
    get sPOID(): string {
        return this.getSPOIDFromCookies();
    }

    /**
     * Gets the non-production port number from browser cookies.
     * 
     * @returns The port number for non-production environments
     * 
     * @remarks
     * This is used in non-production environments to determine the correct port for API calls.
     */
    get nonProdPortNumber(): string {
        return this.getNonProdPortNumberFromCookies();
    }

    /**
     * Extracts the legacy SAT (Security Access Token) cookie value.
     * 
     * @returns The SAT cookie value or undefined if not found or improperly formatted
     * 
     * @remarks
     * The SAT cookie contains a pipe-delimited value; this getter returns the second part.
     */
    get legacySatCookie(): string {
        const cookieValue = this.cookieService.get(COOKIES.MONOLITH_TARGET);
        if (!cookieValue || !cookieValue.includes('|')) {
            return undefined;
        }
        return cookieValue.split('|')[1];
    }

    /**
     * Clears logon-related cookies using the public (unauthenticated) endpoint.
     * 
     * @returns Observable<string[]> - Array of cookie strings to be set/cleared
     * 
     * @remarks
     * This API is used during logout or session cleanup in public flows.
     * The returned cookie strings are automatically processed and set in the browser.
     */
    clearLogonCookiesPublic(): Observable<string[]> {
        const endpoint = `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.CLEAR_LOGON_COOKIES}`;
        return this.clearLogonCookies(endpoint);
    }

    /**
     * Makes the API call to clear logon cookies.
     * 
     * @param endpoint - The API endpoint URL for clearing cookies
     * @returns Observable<string[]> - Array of cookie strings from the API response
     * 
     * @private
     */
    private clearLogonCookies(endpoint: string): Observable<string[]> {
        return this.httpClient
            .post<string[]>(endpoint, {})
            .pipe(
                tap(data => {
                    this.handleCookies(data);
                }),
                catchError(error => {
                    this.logger.error(
                        `${ZciCookieService.name}: error while clearing logon cookies`,
                        error,
                    );
                    return of([]);
                }),
            );
    }

    /**
     * Processes cookie strings from the API response and sets them in the browser.
     * 
     * @param data - Array of cookie strings in "key=value" format
     * 
     * @private
     * @remarks
     * Each cookie string is parsed and set with root path ('/') for application-wide access.
     */
    private handleCookies(data: string[] | undefined): void {
        if (!data) return;

        data.forEach(cookieStr => {
            const [key, value] = cookieStr.split('=').map(s => s.trim());
            if (key && value) {
                this.cookieService.set(key, value, undefined, '/');
            }
        });
    }

    /**
     * Retrieves the SPOID from the CL_SPOID cookie.
     * 
     * @returns The SPOID value or null if not found
     * 
     * @private
     */
    private getSPOIDFromCookies(): string | null {
        const clSpoid = this.cookieService.get(COOKIES.CL_SPOID);

        if (!clSpoid) {
            this.logger.warn(`Unable to identify sPOID`);
        }
        return clSpoid || null;
    }

    /**
     * Determines the appropriate port number for non-production environments.
     * 
     * @returns The port number from cookies
     * 
     * @private
     * @remarks
     * Checks both LOGONNONPRODPORT and MONOLITH_TARGET cookies to find the port.
     */
    private getNonProdPortNumberFromCookies() {
        const nonProdPortNumber = this.cookieService.get(COOKIES.LOGONNONPRODPORT);
        const monolithPortNumber = this.cookieService.get(COOKIES.MONOLITH_TARGET);

        if (nonProdPortNumber) {
            this.logger.info('Using non-prod port');
            return nonProdPortNumber;
        } else {
            this.logger.info('Using monolith port');
            return monolithPortNumber;
        }
    }
}
